/* eslint-disable no-continue */
import { formatRule } from "features/languages/utils/rules";
import {
  getMacrosList,
  areMacrosListsIdentical,
  isMacroInvalid,
  makeMacroElement,
  processOriginalTextToHtml,
} from "features/editor/utils/utils";
import { CharacterType } from "features/languages/types";
import type { IValidationError } from "features/editor/types";
import type { Language, ValidationRules } from "features/languages/types";
import type { ValidationTemplate } from "features/languages/types/validation-templates";

function escapeHtml(unsafeString: string) {
  return unsafeString
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#039;")
    .replace(/{/g, "\u007B")
    .replace(/}/g, "\u007D");
}

const getHighlightedError = (text: string, rule: string, index: number) => {
  let result = escapeHtml(text.slice(0, index));
  result += `<b class="highlight-rule">${formatRule(rule)}</b>`;
  result += escapeHtml(text.slice(index + rule.length));

  return result;
};

const isDigit = (text: string, index: number) => {
  const char = text[index];
  return char >= "0" && char <= "9";
};

const extractNumberFromIndex = (text: string, index: number): string => {
  let result = "";

  while (index < text.length && /\d/.test(text[index])) {
    result += text[index];
    // eslint-disable-next-line no-param-reassign
    index++;
  }

  return result.length > 0 ? result : "";
};

// Validate macros
const IS_NOT_IDENTICAL = "Is Not Identical";
const validateMacrosTask = (text: string, originalText: string, errors: IValidationError[]) => {
  try {
    const macros = getMacrosList(text);
    const macrosOriginal = getMacrosList(originalText);

    const isIdentical = areMacrosListsIdentical(macros, macrosOriginal);

    if (!isIdentical) {
      errors.push({
        rule: "Macros are not identical",
        ruleKey: IS_NOT_IDENTICAL,
      });
    }

    macros.forEach(macro => {
      if (isMacroInvalid(macro)) {
        const [macroMap] = processOriginalTextToHtml(originalText);
        const firstChar = macro[0];
        const macroHtml = makeMacroElement({
          value: macro,
          index: macroMap.get(macro)?.index ?? 0,
          total: 0, // any number
          isExtra: true,
        }).outerHTML;

        errors.push({
          rule: `Macro contains invalid symbol "<b>${firstChar}</b>":  ${macroHtml}`,
        });
      }
    });
  } catch (error) {
    console.log("Can't validate macros: ", error);
  }
};

const validateRulesTask = ({
  text,
  validationTemplate,
  validationRules,
  errors,
}: {
  text: string;
  validationTemplate: ValidationTemplate;
  validationRules: ValidationRules;
  errors: IValidationError[];
}) => {
  const checkMatch = (rule: string, message: string, showMultiple = false) => {
    let index = text.indexOf(rule);

    // Loop to find all occurrences if showMultiple is true
    while (index !== -1) {
      errors.push({
        rule: `${message}: ${formatRule(rule)}`,
        highlight: getHighlightedError(text, rule, index),
      });

      // If showMultiple is false, exit after the first match
      if (!showMultiple) break;

      // Find the next occurrence
      index = text.indexOf(rule, index + 1);
    }
  };

  validationTemplate.rules.forEach((id: number) => {
    const { rule } = validationRules.rules.find(item => item.id === id) ?? {};

    if (!rule) {
      return;
    }

    checkMatch(rule, "Found invalid sequence");
  });

  // TODO: move to validation rules to server
  ["\n", "\r", "\t"].forEach(char => {
    checkMatch(char, "Found invalid character", true);
  });
};

const validateAdditionalRulesTask = ({
  text,
  originalText,
  validationTemplate,
  validationRules,
  errors,
}: {
  text: string;
  originalText: string;
  validationTemplate: ValidationTemplate;
  validationRules: ValidationRules;
  errors: IValidationError[];
}) => {
  // Check for additional validation rules 1
  if (validationTemplate.additionalRules.isStartEndValidationEnabled) {
    const { startDisallowedCharacters, endDisallowedCharacters } = validationRules.additionalRules;

    startDisallowedCharacters.forEach(rule => {
      if (text.startsWith(rule)) {
        errors.push({
          rule: `Start character "${formatRule(rule)}" is not allowed.`,
        });
      }
    });

    endDisallowedCharacters.forEach(rule => {
      if (text.endsWith(rule)) {
        errors.push({
          rule: `End character "${formatRule(rule)}" is not allowed.`,
        });
      }
    });
  }

  const macros = getMacrosList(text);

  // Check for additional validation rules 2
  if (validationTemplate.additionalRules.isSpecialCharValidationEnabled) {
    const {
      beforeSpecialCharactersDisallowed: beforeSpecialCharactersAllowed,
      afterSpecialCharactersDisallowed: afterSpecialCharactersAllowed,
    } = validationRules.additionalRules;
    const beforeSpecial = ["<", "{"];
    const afterSpecial = [">", "}"];

    // Mask to skip validation of macros in the text
    const charsToValidateMask = new Array(text.length - 1).fill(1);
    let position = 0;

    macros.forEach(macro => {
      const index = text.indexOf(macro, position);
      position = index + macro.length;

      if (macro.length > 1) {
        charsToValidateMask.fill(0, index + 1, position - 1);
      }
    });

    // eslint-disable-next-line no-plusplus
    for (let i = 0; i < text.length; i++) {
      if (!charsToValidateMask[i]) {
        continue;
      }

      const char = text[i];

      if (beforeSpecial.includes(char)) {
        const charPrev = text[i - 1];

        if (charPrev && !beforeSpecialCharactersAllowed.includes(charPrev)) {
          errors.push({
            rule: `"${charPrev}" is not allowed before "${char}".`,
            highlight: getHighlightedError(text, charPrev, i - 1),
          });
        }
      }

      if (afterSpecial.includes(char)) {
        const charNext = text[i + 1];

        if (charNext && !afterSpecialCharactersAllowed.includes(charNext)) {
          errors.push({
            rule: `"${charNext}" is not allowed after "${char}".`,
            highlight: getHighlightedError(text, charNext, i + 1),
          });
        }
      }
    }
  }

  if (
    validationTemplate.additionalRules.isSameNumberAfterMacro &&
    !errors.some(error => error?.ruleKey === IS_NOT_IDENTICAL)
  ) {
    let position = 0;
    let positionOriginal = 0;

    macros.forEach(macro => {
      const index = text.indexOf(macro, position);
      const indexOriginal = originalText.indexOf(macro, positionOriginal);
      position = index + macro.length;
      positionOriginal = indexOriginal + macro.length;

      if (isDigit(text, position) || isDigit(originalText, positionOriginal)) {
        const number = extractNumberFromIndex(text, position);
        const originalNumber = extractNumberFromIndex(originalText, positionOriginal);

        if (number !== originalNumber) {
          const [macroMap] = processOriginalTextToHtml(originalText);
          const macroHtml = makeMacroElement({
            value: macro,
            index: macroMap.get(macro)?.index ?? 0,
            total: 0, // any number
            isExtra: true,
          }).outerHTML;

          errors.push({
            rule: `Numbers are different after macro ${macroHtml}: "${originalNumber}" in original, "${number}" in translation`,
            highlight: getHighlightedError(text, `${number}`, position),
          });
        }
      }
    });
  }
};

// Validate cyrillic symbols
const validateCyrillicSymbols = (text: string, errors: IValidationError[]) => {
  // Regular expression to match Cyrillic characters
  const cyrillicRegex = /[\u0400-\u04FF]/g;
  const cyrillicSymbolsWithIndices = [];

  // eslint-disable-next-line no-restricted-syntax
  for (const match of text.matchAll(cyrillicRegex)) {
    cyrillicSymbolsWithIndices.push({
      symbol: match[0],
      index: match.index,
    });
  }

  cyrillicSymbolsWithIndices.forEach(({ symbol, index }) => {
    errors.push({
      rule: "Cyrillic characters are not allowed",
      highlight: getHighlightedError(text, symbol, index),
    });
  });
};

export const validateItem = ({
  text,
  originalText,
  languageTranslation: language,
  validationTemplate,
  validationRules,
}: {
  text: string | undefined;
  originalText: string;
  languageTranslation: Language;
  validationTemplate: ValidationTemplate;
  validationRules: ValidationRules;
}) => {
  const errors: IValidationError[] = [];

  // Empty text
  if (!text) {
    errors.push({
      rule: "Empty",
    });

    return errors;
  }

  // Validate macros
  validateMacrosTask(text, originalText, errors);

  // Validation rules
  validateRulesTask({ text, validationTemplate, validationRules, errors });

  // Additional validation rules
  validateAdditionalRulesTask({ text, originalText, validationTemplate, validationRules, errors });

  // Validate cyrillic symbols
  if (language.characterType !== CharacterType.Cyrillic) {
    validateCyrillicSymbols(text, errors);
  }

  return errors;
};
